import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthProvider';
import LoginForm from './LoginForm';
import SignupForm from './SignupForm';
import ChatRoom from './ChatRoom';
import RoomSelectionModal from './RoomSelectionModal';
import { useNarrativeRooms } from '../shared/hooks/useNarrativeRooms';
import { FaTrash } from 'react-icons/fa';
import api from '../utils/api';

/**
 * Desktop App Root Component
 * Contains the original desktop UI functionality
 */
const DesktopRoot = () => {
  const { user, signOut, loading } = useAuth();
  const [narratives, setNarratives] = useState([]);
  const [allNarratives, setAllNarratives] = useState([]);
  const [showingMoreStories, setShowingMoreStories] = useState(false);
  const [loadingNarratives, setLoadingNarratives] = useState(true);
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [selectedNarrative, setSelectedNarrative] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('');
  const [currentRoom, setCurrentRoom] = useState(null);
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const [roomSelectionData, setRoomSelectionData] = useState(null);
  const [showRoomSelection, setShowRoomSelection] = useState(false);
  const [processingInvite, setProcessingInvite] = useState(false);
  const [inviteLoadingMessage, setInviteLoadingMessage] = useState('Loading story room...');

  const genres = ['Adventure', 'Mystery', 'Fantasy', 'Sci-Fi', 'Horror', 'Romance', 'Western', 'Thriller', 'Comedy'];

  // Memoized room creation functions for stable references
  const createStableRoom = useCallback((apiRoom, narrative, isGuest = false, additionalProps = {}) => {
    return {
      ...apiRoom,
      narrative,
      isGuest,
      ...additionalProps
    };
  }, []);

  // Stable onLeave callback to prevent ChatRoom re-renders
  const handleLeaveRoom = useCallback(() => {
    setCurrentRoom(null);
  }, []);

  // Memoized current room to prevent unnecessary re-renders
  const stableCurrentRoom = useMemo(() => {
    if (!currentRoom) return null;
    
    if (currentRoom.narrative && typeof currentRoom.id !== 'undefined') {
      return currentRoom;
    }
    
    return currentRoom;
  }, [
    currentRoom, // Include currentRoom itself to catch all changes
    currentRoom?.id, 
    currentRoom?.narrative?.id, 
    currentRoom?.isGuest,
    currentRoom?.guest_user_id
  ]);

  // Function to handle room invite joins via /join/{room_id} URLs
  const handleRoomInvite = async (roomId, prefillName) => {
    try {
      setProcessingInvite(true);
      setInviteLoadingMessage('Loading story room...');

      // First, verify the room exists and get its details
      const roomResponse = await api.get(`/scenes/validate/${roomId}`);

      if (roomResponse.narrative?.title) {
        setInviteLoadingMessage(`Loading ${roomResponse.narrative.title}...`);
      }

      if (roomResponse && roomResponse.scene && roomResponse.scene.room_id) {
        const narrativeTitle = roomResponse.narrative?.title || 'Unknown Story';
        const narrativeData = roomResponse.narrative || { id: 'unknown', title: narrativeTitle };

        if (user) {
          // Authenticated path: join as real user — add to participants, use JWT for WS
          setInviteLoadingMessage(`Joining ${narrativeTitle}...`);
          await api.joinRoom({ room_id: roomId });

          const roomData = {
            id: roomResponse.scene.room_id,
            name: roomResponse.room_data?.name || narrativeTitle,
            room_code: roomResponse.room_data?.room_code || null,
            isGuest: false,
          };

          const stableRoom = createStableRoom(roomData, narrativeData, false, { isInviteJoin: true, roomResponse });
          window.history.replaceState({}, document.title, '/');
          setCurrentRoom(stableRoom);
          setProcessingInvite(false);
          return true;

        } else {
          // Guest path: create/get guest identity, use guest token for WS
          setInviteLoadingMessage('Creating guest user...');

          const existingGuest = sessionStorage.getItem('guest_user') ? JSON.parse(sessionStorage.getItem('guest_user')) : null;
          const guestResponse = await api.createOrGetGuestUser({
            room_id: roomId,
            existing_guest_id: existingGuest?.id || null,
            display_name: existingGuest ? null : (prefillName || null),
          });

          if (!guestResponse.success) {
            throw new Error('Failed to create/get guest user');
          }

          const guestUser = guestResponse.guest_user;
          const context = guestResponse.context;

          sessionStorage.setItem('guest_user', JSON.stringify(guestUser));
          setInviteLoadingMessage(`Joining ${narrativeTitle}...`);

          sessionStorage.setItem('guestSession', JSON.stringify({
            user: guestUser,
            session: { access_token: `guest_${guestUser.id}`, user: guestUser }
          }));

          const roomData = {
            id: roomResponse.scene.room_id,
            name: roomResponse.room_data?.name || context.scene_title || roomResponse.scene.state_data?.scene_title || `Room ${roomResponse.scene.room_id.substr(0, 8)}`,
            room_code: roomResponse.room_data?.room_code || null,
            guest_user_id: guestUser.id,
            isGuest: true,
          };

          const guestNarrativeData = roomResponse.narrative || context.narrative || context.inferred_narrative || { id: 'unknown', title: 'Unknown Story' };

          const stableRoom = createStableRoom(roomData, guestNarrativeData, true, { isInviteJoin: true, roomResponse, guest_user_id: guestUser.id });
          window.history.replaceState({}, document.title, '/');
          setCurrentRoom(stableRoom);
          setProcessingInvite(false);
          return true;
        }

      } else {
        throw new Error('Room not found or invalid response format');
      }
    } catch (error) {
      console.error('Error in handleRoomInvite:', error);
      setProcessingInvite(false);
      setInviteLoadingMessage('Loading story room...');
      window.history.replaceState({}, document.title, '/');
      throw error;
    }
  };

  useEffect(() => {
    loadNarratives();
    
    // Handle room invite URLs and email confirmation
    const handleURLParameters = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const pathname = window.location.pathname;
      
      const roomInviteMatch = pathname.match(/\/join\/([a-f0-9-]{36})/);
      if (roomInviteMatch) {
        const inviteRoomId = roomInviteMatch[1];
        const prefillName = urlParams.get('name') || null;

        try {
          await handleRoomInvite(inviteRoomId, prefillName);
          return;
        } catch (error) {
          console.error('Error handling room invite join:', error);
        }
      }
      
      // Handle email confirmation from URL parameters
      const token = urlParams.get('token');
      const type = urlParams.get('type');
      const confirmed = urlParams.get('confirmed');
      
      if (token && type === 'signup') {
        setEmailConfirmed(true);
        window.history.replaceState({}, document.title, window.location.pathname);
      } else if (confirmed === 'true') {
        setEmailConfirmed(true);
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      
      const error = urlParams.get('error');
      const errorDescription = urlParams.get('error_description');
      if (error) {
        console.error('❌ Email confirmation error:', error, errorDescription);
      }
    };
    
    handleURLParameters();
  }, []);

  const loadNarratives = async () => {
    try {
      setLoadingNarratives(true);
      console.log('🔍 DESKTOP: Starting to load narratives...');
      
      // Use fast endpoint for quick loading, activity indicators will be approximate
      const response = await api.getNarratives({ limit: 100, genre: selectedGenre });
      
      console.log('🔍 DESKTOP: API response received:', {
        success: response.success,
        narrativeCount: response.narratives?.length || 0,
        firstNarrative: response.narratives?.[0]?.title || 'none'
      });
      
      if (response.success) {
        setAllNarratives(response.narratives);
        // Show ALL narratives for carousel display
        setNarratives(response.narratives);
        console.log('🔍 DESKTOP: Set narratives state with', response.narratives.length, 'stories');
      } else {
        console.error('❌ DESKTOP: API returned success: false');
      }
    } catch (error) {
      console.error('❌ DESKTOP: Error loading narratives:', error);
      setNarratives([]);
      setAllNarratives([]);
    } finally {
      setLoadingNarratives(false);
      console.log('🔍 DESKTOP: Loading narratives complete');
    }
  };

  const handleSearch = async () => {
    if (searchTerm.trim() || selectedGenre) {
      try {
        const response = await api.searchNarratives(searchTerm, selectedGenre, 100);
        if (response.success) {
          setNarratives(response.narratives);
        }
      } catch (error) {
        console.error('Error searching narratives:', error);
      }
    } else {
      loadNarratives();
    }
  };

  const handleShowMoreStories = () => {
    // Carousel approach - this function is no longer needed
    // All stories are shown in horizontal carousels per genre
  };

  // Carousel navigation functions
  const scrollCarousel = (genre, direction) => {
    const carousel = document.getElementById(`carousel-${genre}`);
    if (carousel) {
      const cardWidth = 320; // Width of each card + gap
      const scrollAmount = cardWidth * 3; // Scroll 3 cards at a time
      const currentScroll = carousel.scrollLeft;
      const newScroll = direction === 'left' 
        ? Math.max(0, currentScroll - scrollAmount)
        : currentScroll + scrollAmount;
      
      carousel.scrollTo({
        left: newScroll,
        behavior: 'smooth'
      });
    }
  };

  // Get featured stories (mix of popular and diverse)
  const getFeaturedStories = (allNarratives) => {
    if (!allNarratives || allNarratives.length === 0) return [];
    
    // Get first 4 as "popular" and next 4 as "diverse"
    const popular = allNarratives.slice(0, 4);
    const diverse = allNarratives.slice(4, 8);
    
    return [...popular, ...diverse];
  };

  const createNewRoomForNarrative = async (narrative) => {
    try {
      const response = await api.createRoom({
        room_catalog_id: narrative.id,
        is_private: false
      });

      if (response.success) {
        const stableRoom = createStableRoom(response.room, narrative);
        setCurrentRoom(stableRoom);
      } else {
        console.error('❌ DESKTOP: Room creation failed:', response);
        throw new Error('Failed to create room');
      }
    } catch (error) {
      console.error('❌ DESKTOP: Room creation error:', error);
    }
  };

  const handleJoinAsGuest = async (narrative) => {
    
    // Step 1: Get existing guest user from sessionStorage for reuse check
    const storedGuestUser = sessionStorage.getItem('guest_user');
    let existingGuestId = null;
    
    if (storedGuestUser) {
      try {
        const guestUser = JSON.parse(storedGuestUser);
        existingGuestId = guestUser.id;
        
        // Check for existing rooms with this guest user
        const roomsResponse = await api.getGuestRoomsForNarrative(narrative.id, guestUser.id);
        
        if (roomsResponse.success && roomsResponse.rooms.length > 0) {
          // Always show room selection modal when rooms exist (even for 1 room)
          setRoomSelectionData({
            narrative: narrative,
            rooms: roomsResponse.rooms,
            isGuest: true,
            guestUser: guestUser
          });
          setShowRoomSelection(true);
          return;
        }
      } catch (error) {
        console.warn('❌ DESKTOP: Error checking existing guest rooms:', error);
        existingGuestId = null; // Reset if there was an error
      }
    }

    // Step 2: Use unified guest creation approach
    
    try {
      // NEW UNIFIED APPROACH - Get backend guest user first
      const guestResponse = await api.createOrGetGuestUser({
        narrative_id: narrative.id,
        existing_guest_id: existingGuestId
      });
      
      if (!guestResponse.success) {
        throw new Error('Failed to create/get unified guest user');
      }
      
      const guestUser = guestResponse.guest_user;
      const context = guestResponse.context;
      
      // Store backend-created guest user
      sessionStorage.setItem('guest_user', JSON.stringify(guestUser));
      
      // Step 3: Create room using unified guest room creation
      const roomResponse = await api.createGuestRoomUnified(guestUser.id, {
        room_catalog_id: narrative.room_catalog_id || narrative.id,
        is_private: false
      });

      if (roomResponse.success) {
        console.log('🏠 DESKTOP: Room created successfully, setting currentRoom:', {
          roomId: roomResponse.room.id,
          roomName: roomResponse.room.name,
          narrativeTitle: narrative.title,
          guestUserId: guestUser.id
        });
        
        const stableRoom = createStableRoom(
          roomResponse.room,
          narrative,
          true,
          { guest_user_id: guestUser.id }
        );
        setCurrentRoom(stableRoom);
        
        console.log('🏠 DESKTOP: currentRoom state updated, should trigger navigation to ChatRoom');
        
      } else {
        throw new Error('Failed to create room with unified guest user');
      }
      
    } catch (error) {
      console.error('❌ DESKTOP: Unified guest room creation error:', error);
      console.error('❌ DESKTOP: Error details:', {
        message: error.message,
        response: error.response,
        stack: error.stack
      });
      
      // Show user-friendly error instead of white screen
      alert(`Failed to create guest room: ${error.message}. Please try again.`);
    }
  };

  const handleNarrativeSelect = async (narrative) => {
    
    if (user) {
      try {
        // Get current user ID from auth context
        const userId = user.id;
        
        if (!userId) {
          console.log('❌ DESKTOP: No user ID available for room discovery');
          await createNewRoomForNarrative(narrative);
          return;
        }
        
        const roomsResponse = await api.getUserRoomsForNarrative(narrative.id);
        
        if (roomsResponse.success) {
          const existingRooms = roomsResponse.rooms || [];
          
          if (existingRooms.length === 0) {
            await createNewRoomForNarrative(narrative);
          } else {
            // Always show room selection modal when rooms exist (even for 1 room)
            // This allows user to see existing room details and choose to continue or start new
            setRoomSelectionData({
              narrative: narrative,
              rooms: existingRooms
            });
            setShowRoomSelection(true);
          }
        } else {
          await createNewRoomForNarrative(narrative);
        }
      } catch (error) {
        console.error('❌ DESKTOP: Room discovery error:', error);
        await createNewRoomForNarrative(narrative);
      }
    } else {
      await handleJoinAsGuest(narrative);
    }
  };

  const handleAuthSuccess = () => {
    setShowAuth(false);
    if (selectedNarrative) {
      handleNarrativeSelect(selectedNarrative);
      setSelectedNarrative(null);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('DESKTOP: Error signing out:', error);
    }
  };

  const handleDeleteRoom = async (roomId, roomName) => {
    if (!window.confirm(`Are you sure you want to delete "${roomName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.deleteRoom(roomId);
      
      // Refresh the room list by refetching
      if (roomSelectionData) {
        const userId = user.id;
        const roomsResponse = await api.getUserRoomsForNarrative(roomSelectionData.narrative.id);
        
        if (roomsResponse.success) {
          const updatedRooms = roomsResponse.rooms || [];
          
          if (updatedRooms.length === 0) {
            // No rooms left - close modal and create new room
            setShowRoomSelection(false);
            setRoomSelectionData(null);
            await createNewRoomForNarrative(roomSelectionData.narrative);
          } else if (updatedRooms.length === 1) {
            // Only one room left - auto-join it
            setShowRoomSelection(false);
            setRoomSelectionData(null);
            const stableRoom = createStableRoom(updatedRooms[0], roomSelectionData.narrative);
            setCurrentRoom(stableRoom);
          } else {
            // Still multiple rooms - update the list
            setRoomSelectionData(prev => ({
              ...prev,
              rooms: updatedRooms
            }));
          }
        }
      }
    } catch (error) {
      console.error('❌ DESKTOP: Error deleting room:', error);
      alert('Failed to delete room. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  // Show chat room if user has joined a story
  if (stableCurrentRoom) {
    const handleGuestSignIn = () => {
      setAuthMode('signup');
      setShowAuth(true);
    };
    
    return (
      <>
        <ChatRoom room={stableCurrentRoom} user={user} onLeave={handleLeaveRoom} onSignIn={handleGuestSignIn} />
        
        {/* Auth Modal */}
        {showAuth && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="relative">
              <button
                onClick={() => setShowAuth(false)}
                className="absolute -top-4 -right-4 bg-gray-700 hover:bg-gray-600 text-white w-8 h-8 rounded-full flex items-center justify-center z-10"
              >
                ×
              </button>
              {authMode === 'login' ? (
                <LoginForm
                  onClose={handleAuthSuccess}
                  onSwitchToSignup={() => setAuthMode('signup')}
                />
              ) : (
                <SignupForm
                  onClose={handleAuthSuccess}
                  onSwitchToLogin={() => setAuthMode('login')}
                />
              )}
            </div>
          </div>
        )}

        {/* Room Selection Modal */}
        {showRoomSelection && roomSelectionData && (
          <RoomSelectionModal
            isOpen={showRoomSelection}
            onClose={() => {
              setShowRoomSelection(false);
              setRoomSelectionData(null);
            }}
            rooms={roomSelectionData.rooms}
            narrativeName={roomSelectionData.narrative?.title || 'Adventure'}
            onJoinRoom={async (room) => {
              try {
                const stableRoom = createStableRoom(room, roomSelectionData.narrative);
                setCurrentRoom(stableRoom);
                setShowRoomSelection(false);
                setRoomSelectionData(null);
              } catch (error) {
                console.error('Error joining room:', error);
                alert('Failed to join room. Please try again.');
              }
            }}
            onDeleteRoom={async (roomId) => {
              try {
                await api.deleteRoom(roomId);
                // Refresh the room list
                const updatedRooms = roomSelectionData.rooms.filter(r => r.id !== roomId);
                if (updatedRooms.length === 0) {
                  // No rooms left - close modal and create new room
                  setShowRoomSelection(false);
                  setRoomSelectionData(null);
                  await createNewRoomForNarrative(roomSelectionData.narrative);
                } else if (updatedRooms.length === 1) {
                  // Only one room left - auto-join it
                  setShowRoomSelection(false);
                  setRoomSelectionData(null);
                  const stableRoom = createStableRoom(updatedRooms[0], roomSelectionData.narrative);
                  setCurrentRoom(stableRoom);
                } else {
                  // Still multiple rooms - update the list
                  setRoomSelectionData(prev => ({
                    ...prev,
                    rooms: updatedRooms
                  }));
                }
              } catch (error) {
                console.error('Error deleting room:', error);
                alert('Failed to delete room. Please try again.');
              }
            }}
            onCreateNew={async () => {
              try {
                setShowRoomSelection(false);
                setRoomSelectionData(null);
                await createNewRoomForNarrative(roomSelectionData.narrative);
              } catch (error) {
                console.error('Error creating new room:', error);
                alert('Failed to create new room. Please try again.');
              }
            }}
          />
        )}
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <header className="bg-gray-800 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <h1 className="text-3xl font-bold text-white">🎭 PlayScreen.ai</h1>
            <span className="text-blue-400 text-sm bg-blue-900/30 px-2 py-1 rounded">Desktop Edition</span>
          </div>
          <div className="flex items-center space-x-4">
            {user ? (
              <div className="flex items-center space-x-3">
                <span className="text-gray-300">Welcome, {user.email}</span>
                <button
                  onClick={handleSignOut}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition duration-200"
                >
                  Sign Out
                </button>
              </div>
            ) : (
              <button
                onClick={() => {
                  setAuthMode('login');
                  setShowAuth(true);
                }}
                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg transition duration-200"
              >
                Sign In
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Auth Modal */}
      {showAuth && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="relative">
            <button
              onClick={() => setShowAuth(false)}
              className="absolute -top-4 -right-4 bg-gray-700 hover:bg-gray-600 text-white w-8 h-8 rounded-full flex items-center justify-center z-10"
            >
              ×
            </button>
            {authMode === 'login' ? (
              <LoginForm
                onClose={handleAuthSuccess}
                onSwitchToSignup={() => setAuthMode('signup')}
              />
            ) : (
              <SignupForm
                onClose={handleAuthSuccess}
                onSwitchToLogin={() => setAuthMode('login')}
              />
            )}
          </div>
        </div>
      )}

      {/* Main Content - Original Desktop Layout */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Show loading spinner when processing invite instead of main content */}
        {processingInvite ? (
          <div className="flex flex-col items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-6"></div>
              <h2 className="text-3xl font-bold text-white mb-4">🎭 PlayScreen</h2>
              <p className="text-gray-300 text-xl">{inviteLoadingMessage}</p>
            </div>
          </div>
        ) : (
          <>
            {/* Email Confirmation Message */}
            {emailConfirmed && (
              <div className="mb-8 bg-green-500/20 border border-green-500 text-green-400 px-6 py-4 rounded-lg text-center">
                <p className="text-lg">✅ Email confirmed successfully! You can now sign in.</p>
                <button
                  onClick={() => {
                    setEmailConfirmed(false);
                    setShowAuth(true);
                    setAuthMode('login');
                  }}
                  className="mt-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition duration-200"
                >
                  Sign In Now
                </button>
              </div>
            )}

            {/* Hero Section */}
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
                PlayScreen
              </h1>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                This is your story. Shape epic adventures where every choice matters and legendary characters respond to your decisions.
              </p>
            </div>

        {/* Room Selection Modal */}
        {showRoomSelection && roomSelectionData && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-lg max-w-3xl w-full max-h-[85vh] overflow-hidden shadow-xl border border-gray-600">
              {/* Header */}
              <div className="bg-gray-700 p-6 border-b border-gray-600">
                <h2 className="text-xl font-semibold text-white mb-2">
                  Choose Your Adventure
                </h2>
                <p className="text-gray-300 text-sm">
                  Multiple ongoing adventures found for <span className="font-medium">"{roomSelectionData.narrative.title}"</span>
                </p>
              </div>
              
              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(85vh-200px)]">
                <div className="space-y-3 mb-6">
                  {roomSelectionData.rooms.map((room) => (
                    <div 
                      key={room.id}
                      className="bg-gray-700 rounded-lg border border-gray-600 hover:border-gray-500 cursor-pointer transition-all duration-200 hover:bg-gray-650"
                      onClick={() => {
                        if (roomSelectionData.isGuest) {
                          const stableRoom = createStableRoom(
                            room, 
                            roomSelectionData.narrative, 
                            true, 
                            { guest_user_id: roomSelectionData.guestUser.id }
                          );
                          setCurrentRoom(stableRoom);
                        } else {
                          const stableRoom = createStableRoom(room, roomSelectionData.narrative);
                          setCurrentRoom(stableRoom);
                        }
                        setShowRoomSelection(false);
                        setRoomSelectionData(null);
                      }}
                    >
                      <div className="p-4">
                        <div className="flex justify-between items-start mb-3">
                          <h3 className="font-medium text-white text-base">
                            {room.name}
                          </h3>
                          <div className="flex items-start gap-2">
                            <div className="flex flex-col items-end gap-1">
                              <span className="bg-gray-600 text-gray-300 px-2 py-1 rounded text-xs">
                                {room.participant_names?.length || 0} player{(room.participant_names?.length || 0) !== 1 ? 's' : ''}
                              </span>
                              <span className="bg-blue-600 text-white px-2 py-1 rounded text-xs">
                                {room.message_count || 0} messages
                              </span>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent room join
                                handleDeleteRoom(room.id, room.name);
                              }}
                              className="text-gray-400 hover:text-red-400 transition-colors p-1"
                              title="Delete room"
                            >
                              <FaTrash className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        
                        <div className="text-sm text-gray-400 mb-3">
                          <span className="text-gray-300">Players:</span> {room.participant_names.join(', ')}
                        </div>
                        
                        {room.last_message_preview && (
                          <div className="bg-gray-800 rounded p-3 mb-3 border-l-2 border-gray-500">
                            <div className="text-xs text-gray-500 mb-1">Latest message:</div>
                            <div className="text-sm text-gray-300">"{room.last_message_preview}"</div>
                          </div>
                        )}
                        
                        <div className="flex justify-between items-center text-xs text-gray-500">
                          <span>
                            Last activity: {new Date(room.last_activity).toLocaleDateString()} at {new Date(room.last_activity).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          </span>
                          <span className="text-gray-400">
                            Click to continue →
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Footer */}
              <div className="bg-gray-700 px-6 py-4 border-t border-gray-600">
                <div className="flex gap-3">
                  <button
                    onClick={async () => {
                      setShowRoomSelection(false);
                      
                      if (roomSelectionData.isGuest) {
                        try {
                          const guestRoomResponse = await api.createGuestRoom({
                            room_catalog_id: roomSelectionData.narrative.id,
                            is_private: false,
                            guest_user_id: roomSelectionData.guestUser.id
                          });

                          if (guestRoomResponse.success) {
                            if (guestRoomResponse.guest_user) {
                              sessionStorage.setItem('guest_user', JSON.stringify(guestRoomResponse.guest_user));
                            }
                            
                            const stableRoom = createStableRoom(
                              guestRoomResponse.room,
                              roomSelectionData.narrative,
                              true,
                              { guest_user_id: guestRoomResponse.guest_user_id }
                            );
                            setCurrentRoom(stableRoom);
                          }
                        } catch (error) {
                          console.error('Error creating new guest room:', error);
                        }
                      } else {
                        await createNewRoomForNarrative(roomSelectionData.narrative);
                      }
                      
                      setRoomSelectionData(null);
                    }}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded font-medium transition duration-200"
                  >
                    Start New Adventure
                  </button>
                  <button
                    onClick={() => {
                      setShowRoomSelection(false);
                      setRoomSelectionData(null);
                    }}
                    className="px-6 bg-gray-600 hover:bg-gray-500 text-gray-300 hover:text-white py-2 rounded font-medium transition duration-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Narratives by Genre - Horizontal Carousel Layout */}
        {(() => {
          const narrativesByGenre = narratives.reduce((acc, narrative) => {
            const genre = narrative.genre || 'Other';
            if (!acc[genre]) {
              acc[genre] = [];
            }
            acc[genre].push(narrative);
            return acc;
          }, {});

          // Get smart featured stories (8 total)
          const featuredNarratives = getFeaturedStories(narratives);
          
          console.log('🔍 DESKTOP: Rendering with', {
            narrativesLength: narratives.length,
            featuredNarrativesLength: featuredNarratives.length,
            loadingNarratives,
            selectedGenre
          });

          return (
            <div>
              {/* Featured Carousel Section */}
              {featuredNarratives.length > 0 && (
                <div className="mb-16">
                  <div className="flex items-center mb-6">
                    <h2 className="text-3xl font-bold text-white mr-4">🌟 Featured Stories</h2>
                    <div className="flex-1 h-px bg-gradient-to-r from-blue-500 to-transparent"></div>
                    <span className="text-gray-400 text-sm ml-4">{featuredNarratives.length} curated stories</span>
                  </div>
                  
                  {/* Featured Carousel Container */}
                  <div className="relative group">
                    {/* Left Arrow */}
                    <button
                      onClick={() => scrollCarousel('featured', 'left')}
                      className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-lg"
                      style={{ marginLeft: '-20px', transform: 'translateY(-50%)' }}
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>

                    {/* Right Arrow */}
                    <button
                      onClick={() => scrollCarousel('featured', 'right')}
                      className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-lg"
                      style={{ marginRight: '-20px', transform: 'translateY(-50%)' }}
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>

                    {/* Horizontal Scrolling Container */}
                    <div
                      id="carousel-featured"
                      className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth py-4 px-6"
                      style={{
                        scrollSnapType: 'x mandatory',
                        maskImage: 'linear-gradient(to right, transparent, black 40px, black calc(100% - 40px), transparent)',
                        WebkitMaskImage: 'linear-gradient(to right, transparent, black 40px, black calc(100% - 40px), transparent)'
                      }}
                    >
                      {featuredNarratives.map((narrative, index) => (
                        <div
                          key={narrative.id}
                          className="flex-none w-80 bg-gray-800 rounded-xl shadow-xl overflow-hidden hover:transform hover:scale-105 hover:shadow-[0_0_15px_rgba(250,204,21,0.7)] transition duration-500 relative"
                          style={{ scrollSnapAlign: 'start' }}
                        >
                          {/* Featured Badge */}
                          {index < 4 && (
                            <div className="absolute top-4 right-4 z-10">
                              <span className="bg-gradient-to-r from-yellow-500 to-orange-500 text-black px-2 py-1 rounded-full text-xs font-bold">
                                {index < 2 ? '🔥 TRENDING' : '⭐ POPULAR'}
                              </span>
                            </div>
                          )}
                          
                          {/* Image */}
                          <div className="relative h-48 overflow-hidden">
                            <img
                              src={narrative.image_url}
                              alt={narrative.title}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent"></div>
                            <div className="absolute top-4 left-4">
                              <span className="bg-gray-700 text-gray-200 px-3 py-1 rounded-full text-sm font-medium">
                                {narrative.genre}
                              </span>
                            </div>
                          </div>

                          {/* Content */}
                          <div className="p-6 bg-gray-800 flex flex-col h-48 relative">
                            <h3 className="text-lg font-bold text-white mb-2 leading-tight">
                              {narrative.title}
                            </h3>
                            
                            {/* Character info */}
                            {narrative.featured_character && (
                              <div className="flex items-center mb-2">
                                <span className="text-gray-300 text-xs">🎭 {narrative.featured_character}</span>
                              </div>
                            )}

                            {/* Removed activity indicator - not useful when all show same generic text */}

                            {/* Description - guaranteed 2 lines */}
                            <p className="text-gray-300 text-sm leading-relaxed line-clamp-2 mb-3 h-10">
                              {narrative.description}
                            </p>

                            {/* Join button - fixed position at bottom */}
                            <div className="absolute bottom-4 left-4 right-4">
                              <button
                                onClick={() => handleNarrativeSelect(narrative)}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium transition duration-200"
                              >
                                Join Story
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Genre Carousel Sections */}
              {Object.entries(narrativesByGenre).map(([genre, genreNarratives]) => (
                <div key={genre} className="mb-16">
                  <div className="flex items-center mb-6">
                    <h2 className="text-3xl font-bold text-white mr-4">{genre}</h2>
                    <div className="flex-1 h-px bg-gradient-to-r from-gray-500 to-transparent"></div>
                    <span className="text-gray-400 text-sm ml-4">{genreNarratives.length} stories</span>
                  </div>
                  
                  {/* Carousel Container */}
                  <div className="relative group">
                    {/* Left Arrow */}
                    <button
                      onClick={() => scrollCarousel(genre, 'left')}
                      className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-lg"
                      style={{ marginLeft: '-20px', transform: 'translateY(-50%)' }}
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>

                    {/* Right Arrow */}
                    <button
                      onClick={() => scrollCarousel(genre, 'right')}
                      className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-lg"
                      style={{ marginRight: '-20px', transform: 'translateY(-50%)' }}
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>

                    {/* Horizontal Scrolling Container */}
                    <div
                      id={`carousel-${genre}`}
                      className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth py-4 px-6"
                      style={{
                        scrollSnapType: 'x mandatory',
                        maskImage: 'linear-gradient(to right, transparent, black 40px, black calc(100% - 40px), transparent)',
                        WebkitMaskImage: 'linear-gradient(to right, transparent, black 40px, black calc(100% - 40px), transparent)'
                      }}
                    >
                      {genreNarratives.map((narrative) => (
                        <div
                          key={narrative.id}
                          className="flex-none w-80 bg-gray-800 rounded-xl shadow-xl overflow-hidden hover:transform hover:scale-105 hover:shadow-[0_0_15px_rgba(250,204,21,0.7)] transition duration-500"
                          style={{ scrollSnapAlign: 'start' }}
                        >
                          {/* Image */}
                          <div className="relative h-48 overflow-hidden">
                            <img
                              src={narrative.image_url}
                              alt={narrative.title}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent"></div>
                            <div className="absolute top-4 left-4">
                              <span className="bg-gray-700 text-gray-200 px-3 py-1 rounded-full text-sm font-medium">
                                {narrative.genre}
                              </span>
                            </div>
                          </div>

                          {/* Content */}
                          <div className="p-6 bg-gray-800 flex flex-col h-48">
                            <h3 className="text-xl font-bold text-white mb-3">
                              {narrative.title}
                            </h3>
                            
                            {/* Character info */}
                            {narrative.featured_character && (
                              <div className="flex items-center mb-3">
                                <span className="text-gray-300 text-sm">🎭 {narrative.featured_character}</span>
                              </div>
                            )}

                            {/* Removed activity indicator - not useful when all show same generic text */}

                            {/* Description */}
                            <p className="text-gray-300 text-sm mb-4 leading-relaxed flex-grow line-clamp-3">
                              {narrative.description}
                            </p>

                            {/* Join button */}
                            <div className="mt-auto">
                              <button
                                onClick={() => handleNarrativeSelect(narrative)}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium transition duration-200"
                              >
                                Join Story
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="mt-16 text-center text-gray-400 pb-8">
        <p>🎭 PlayScreen</p>
      </footer>
    </div>
  );
};

export default DesktopRoot;